import { PushPin } from "phosphor-react";
import { useMemo } from "react";
import Box from "../../components/box";
import {
  CommercialLoanDetails,
  RetailODDetails,
  TermLoanDetails,
  WCTLoanDetail
} from "../../dto";
import { LoanType } from "../../models";
import { assetClassificationStatusToTagMap } from "./helper";

interface LoanTagsProps {
  loanDetails:
    TermLoanDetails | RetailODDetails | WCTLoanDetail | CommercialLoanDetails;
  loanType?: LoanType;
}

function getLoanTags(
  loanDetails:
    TermLoanDetails | RetailODDetails | CommercialLoanDetails | WCTLoanDetail,
  loanType: LoanType | undefined,
): Array<{ label: string; color: string }> {
  const loanTags: Array<{ label: string; color: string }> = [];

  const _loanType = loanType ?? loanDetails.loanType;

  switch (_loanType) {
    case "TERM_LOAN": {
      const termLoan = loanDetails as TermLoanDetails;

      // INFO: Commenting this out as per new requirements
      // if (termLoan.isForeclosed && !termLoan.isBalanceTransferred) {
      //   loanTags.push({
      //     label: "Pre Closed (by borrower)",
      //     color: "#e57373",
      //   });
      // }

      if (termLoan.isBookBuyBack && termLoan.consolidatedLoans?.length > 0) {
        loanTags.push({
          label: "Acquiring Loan",
          color: "#81c784",
        });
      }

      if (termLoan.isWriteOff) {
        loanTags.push({
          label: "Loan Written Off",
          color: "#ba68c8",
        });
      }

      if (
        termLoan.consolidatedByLoanId &&
        termLoan.isForeclosed &&
        !termLoan.isBookBuyBack
      ) {
        loanTags.push({
          label: "Pre Closed (by sanction of new loan)",
          color: "#ffb74d",
        });
      }

      if (!termLoan.isBookBuyBack && termLoan.consolidatedLoans?.length > 0) {
        loanTags.push({
          label: "Loan Topup",
          color: "#4db6ac",
        });
      }

      if (termLoan.acquiredByLoanId && termLoan.isForeclosed) {
        loanTags.push({
          label: "Pre Closed (sold off)",
          color: "#90a4ae",
        });
      }

      break;
    }

    case "RETAIL_OD": {
      const retailOd = loanDetails as RetailODDetails;

      if (retailOd.writeOffMetadata) {
        loanTags.push({
          label: "Loan Written Off",
          color: "#f06292",
        });
      }

      break;
    }

    case "WORKING_CAPITAL_TERM_LOAN": {
      const wctlLoan = loanDetails as WCTLoanDetail;

      if (wctlLoan.writeOffMetadata) {
        loanTags.push({
          label: "Loan Written Off",
          color: "#f06292",
        });
      }

      break;
    }

    case "COMMERCIAL_OD": {
      const commercialLoan = loanDetails as CommercialLoanDetails;

      if (commercialLoan.writeOffMetadata) {
        loanTags.push({
          label: "Loan Written Off",
          color: "#f06292",
        });
      }

      break;
    }

    default:
      console.log("loan type not found");
      break;
  }

  if (loanDetails.isNpa) {
    loanTags.push({
      label: "NPA",
      color: "#ff5252",
    });
  }

  if (loanDetails.assetClassificationStatus) {
    const assetClassificationStatusLoanTag =
      assetClassificationStatusToTagMap[loanDetails.assetClassificationStatus];
    if (assetClassificationStatusLoanTag?.label) {
      loanTags.push(assetClassificationStatusLoanTag);
    }
  }

  return loanTags;
}

export default function LoanTags({ loanDetails, loanType }: LoanTagsProps) {
  const tags = useMemo(() => {
    return getLoanTags(loanDetails, loanType);
  }, [loanDetails, loanType]);

  if (tags.length === 0) return null;

  return (
    <Box>
      <div
        style={{
          display: "flex",
          gap: "24px",
          flexDirection: "row-reverse",
          flexWrap: "wrap",
        }}
      >
        {tags.map((tag) => (
          <Tag key={tag.label} {...tag} />
        ))}
      </div>
    </Box>
  );
}

export function Tag({ label, color }: { label: string; color: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        gap: "8px",
      }}
    >
      <PushPin size={20} color={color} weight="fill" />
      <p style={{ color, margin: 0 }}>{label}</p>
    </div>
  );
}
